import { useCallback } from "react"
import PropTypes from "prop-types"
import { capitalize } from "lodash"
import { DESC } from "helpers/sorts"
import { useAssetViewDashboardMetrics } from "helpers/metrics/hooks"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import ContributorsTable from "renderers/contributors-datagrid"
import { CLASSIFICATIONS_SHAPE } from "helpers/shapes/asset-view"
import { useTemplateGatherers } from "connected-components/dashboard/hooks/use-template-gatherers"
import { useAnalytics } from "connected-components/analytics"
import { useAssetViewData } from "connected-components/asset-view/hooks/use-asset-view-data"
import { useAssetViewHeaderHandlers } from "../../hooks/use-asset-view-header-handlers"
import { useAssetViewTemplateChart } from "../../hooks/use-asset-view-template-chart"
import { ASSET_VIEW_BETA_CHART } from "../template-chart-config"

export default function AssetViewBetaComponent({ betaFields, betaInstrument, instrumentsWithMetadata, modelMetadata, highlightedRowIds, disableActiveTrading, instrumentColumns, classifications }) {
  const { selectedMetricDisplay } = useAssetViewTemplateChart(ASSET_VIEW_BETA_CHART)
  const contextData = useAssetViewData()
  const {
    instrument: original,
    base: contextBase,
    groupBy,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    setTradedPosition,
    selectedTableLevels,
    setSelectedTableLevels,
    isExpanded,
    isIntradayMode,
    isEndVsStartDateComparison,
  } = contextData

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useAssetViewHeaderHandlers({ track: analytics.track })

  const gatherers = useTemplateGatherers(contextData, "beta", {
    selectedMetricDisplay,
    betaInstrument,
    betaFields,
    classifications,
  })

  const getBetaDataPath = useCallback((path) => {
    if (selectedMetricDisplay === "betaNetContribution") {
      return `beta.${path}`
    }
    return `beta.securityBeta.${path}`
  }, [selectedMetricDisplay])

  const metrics = useAssetViewDashboardMetrics(
    [{ category: "beta", betaFields, betaInstrument, getBetaDataPath }],
    original,
    rebalanced,
    comparison,
    { instrumentColumns, isActive: !!contextBase, modelMetadata, setTradedPosition, instrumentsWithMetadata, isExpanded, disableActiveTrading, isIntradayEnabled: isIntradayMode, isEndVsStartDateComparison },
  )

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0]].suffix

  return <ContributorsDataIntegrator
    gatherers={gatherers}
    metrics={metrics}
    Renderer={ContributorsTable}
    rendererProps={{
      highlightedRowIds,
      groupByName: groupBy?.label,
      isPositionAttributeGroup: !!groupBy?.isPositionAttribute,
      defaultSort: {
        path: `marketValue${capitalize(defaultSortSuffix)}`,
        direction: DESC,
      },
      downloadKey: "assets.beta",
      downloadName: "Assets - Beta",
      tableNavigationLevels: selectedTableLevels,
      onTableNavigationChange: setSelectedTableLevels,
      onSortChanged: handleSortChanged,
      onTableNavigationChanged: handleTableNavigationChanged,
    }}
  />
}

AssetViewBetaComponent.propTypes = {
  currentDate: PropTypes.string.isRequired,
  betaInstrument: PropTypes.shape({
    securityId: PropTypes.shape({
      internalId: PropTypes.string,
    }),
    resource: PropTypes.shape({
      alias: PropTypes.string,
      type: PropTypes.string,
    }),
    benchmarkId: PropTypes.string,
    indexId: PropTypes.string,
  }),
  betaFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  instrumentsWithMetadata: PropTypes.object.isRequired,
  modelMetadata: PropTypes.object.isRequired,
  highlightedRowIds: PropTypes.arrayOf(PropTypes.string),
  disableActiveTrading: PropTypes.bool.isRequired,
  instrumentColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  classifications: CLASSIFICATIONS_SHAPE.isRequired,
}
